import React, { FC, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useMount } from "react-use";

import { SpinLoader } from "@/components/atoms";
import { StandardLayout } from "@/components/layouts";
import { InfoBox } from "@/components/molecules";
import { FormPanel } from "@/components/organisms";
import { deepEqual } from "@/core/object";
import { requestApi } from "@/shared/apis";
import { Permissions } from "@/shared/domain";
import { useAuth, useBreadcrumb, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import {
    Contact,
    ContactForm,
    DriverLicenseAndId,
    DriverLicenseAndIdForm,
    Finance,
    FinanceForm,
    GeneralInformation,
    GeneralInformationForm,
    Profession,
    ProfessionForm,
} from "../../components";
import { StyledRelationDetailsPage } from "./styled";
import {
    getContact,
    getDriverId,
    getFinance,
    getFormDetailByArea,
    getGeneralDetails,
    getProfession,
    RelationDetailsAreas,
} from "./utils";

const registerFormEditBegin = (detailArea: RelationDetailsAreas) => {
    const tagManagerProps = getFormDetailByArea(detailArea);
    tagManager.pushEditRelationsDetailsBeginEvent(tagManagerProps.id, tagManagerProps.name);
};

const RelationDetailsPage: FC = () => {
    const { user } = useAuth();
    const {
        search: { policyHolderId, relationInternalId, internalpolicyholderId, typePartyId },
    } = useRouterHook();
    const queryClient = useQueryClient();
    const { setPageTitle } = useBreadcrumb();
    const { incUsageCount } = useUserFeedback();
    const { t } = useTranslation();
    const { id: relationId } = useParams<{ id: string }>();

    const allowEditInformationDetails = user.hasPermission(Permissions.AccessEditInformationDetails, policyHolderId);

    const { data: relationDetails, isFetching: isRelationDetailsFetching } = useQuery(
        ["relationDetails", { id: relationId, policyHolderId }],
        () =>
            requestApi.getRelationDetails(
                +relationId,
                Number(policyHolderId),
                user.getAccessRightFor(policyHolderId.toString()).accessControlListId,
                Number(typePartyId),
            ),
        {
            suspense: false,
        },
    );

    useEffect(() => {
        if (relationDetails) {
            setPageTitle(relationDetails.currentDetails.fullName);
        }
    }, [relationDetails]);

    const [currentDetails, pendingDetails] = [relationDetails?.currentDetails, relationDetails?.pendingDetails];

    const { mutate: updateRelationDetails, isLoading: isLoadingUpdateRelationDetails } = useMutation(
        requestApi.updateRelationDetails,
        {
            onSuccess: () => {
                queryClient.invalidateQueries("requestsPaginated");
                queryClient.invalidateQueries("relationDetails");
            },
        },
    );

    useMount(() => {
        incUsageCount();
        tagManager.pushViewRelationsDetailsEvent();
    });

    const submitRelationDetails = (
        formValue: Omit<MyBrokerApi.PersonalDetails, "lastName">,
        detailArea: RelationDetailsAreas,
    ) => {
        const values = {
            ...(relationDetails.pendingDetails || relationDetails.currentDetails),
            ...formValue,
        };

        const params = {
            policyHolderId: +policyHolderId,
            relationDetailsId: +relationId,
            relationInternalId: +relationInternalId,
            internalpolicyholderId: +internalpolicyholderId,
            typePartyId: +typePartyId,
            ibpAclId: user.getAccessRightFor(policyHolderId.toString()).accessControlListId,
            body: values,
        };

        const tagManagerProps = getFormDetailByArea(detailArea);
        try {
            updateRelationDetails(params);
            tagManager.pushEditRelationsDetailsSentEvent(tagManagerProps.id, tagManagerProps.name);
        } catch (e) {
            tagManager.pushEditRelationsDetailsSentEvent(tagManagerProps.id, tagManagerProps.name, false);
            throw e;
        }
    };

    const isLoading = isRelationDetailsFetching || isLoadingUpdateRelationDetails;

    return (
        <StandardLayout title={t("PageTitles.RelationsDetails")}>
            <StyledRelationDetailsPage>
                {currentDetails && <div className="userNameTitle">{currentDetails?.fullName}</div>}
                {pendingDetails && <InfoBox>{t("User.PersonalDetails.PendingInfo")}</InfoBox>}
                {isLoading && <SpinLoader />}
                {!isLoading && (
                    <div className="row">
                        <div className="col">
                            <FormPanel
                                title={t("User.PersonalDetails.GeneralInformation.Title")}
                                mode="read"
                                pending={
                                    pendingDetails &&
                                    !deepEqual(getGeneralDetails(currentDetails), getGeneralDetails(pendingDetails))
                                }
                                renderRead={() => <GeneralInformation details={currentDetails} />}
                                renderEdit={
                                    allowEditInformationDetails
                                        ? (closeEdit) => {
                                              registerFormEditBegin(RelationDetailsAreas.PROFESSION);

                                              return (
                                                  <GeneralInformationForm
                                                      isSubmittable={!isLoading}
                                                      personDetails={pendingDetails || currentDetails}
                                                      onSubmit={(formValue) => {
                                                          submitRelationDetails(
                                                              formValue,
                                                              RelationDetailsAreas.PROFESSION,
                                                          );
                                                          closeEdit();
                                                      }}
                                                  />
                                              );
                                          }
                                        : null
                                }
                            />

                            <FormPanel
                                title={t("User.PersonalDetails.DriverLicenseAndId.Title")}
                                mode="read"
                                pending={
                                    pendingDetails &&
                                    !deepEqual(getDriverId(currentDetails), getDriverId(pendingDetails))
                                }
                                renderRead={() => <DriverLicenseAndId details={currentDetails} />}
                                renderEdit={
                                    allowEditInformationDetails
                                        ? (closeEdit) => {
                                              registerFormEditBegin(RelationDetailsAreas.IDENTITY_AND_DRIVER_LICENSE);

                                              return (
                                                  <DriverLicenseAndIdForm
                                                      isSubmittable={!isLoading}
                                                      personDetails={pendingDetails || currentDetails}
                                                      onSubmit={(formValue) => {
                                                          submitRelationDetails(
                                                              formValue,
                                                              RelationDetailsAreas.IDENTITY_AND_DRIVER_LICENSE,
                                                          );
                                                          closeEdit();
                                                      }}
                                                  />
                                              );
                                          }
                                        : null
                                }
                            />
                            <FormPanel
                                title={t("User.PersonalDetails.Finance.Title")}
                                mode="read"
                                pending={
                                    pendingDetails && !deepEqual(getFinance(currentDetails), getFinance(pendingDetails))
                                }
                                renderRead={() => <Finance details={currentDetails} />}
                                renderEdit={
                                    allowEditInformationDetails
                                        ? (closeEdit) => {
                                              registerFormEditBegin(RelationDetailsAreas.FINANCE);

                                              return (
                                                  <FinanceForm
                                                      isSubmittable={!isLoading}
                                                      personDetails={pendingDetails || currentDetails}
                                                      onSubmit={(formValue) => {
                                                          submitRelationDetails(
                                                              formValue,
                                                              RelationDetailsAreas.FINANCE,
                                                          );
                                                          closeEdit();
                                                      }}
                                                  />
                                              );
                                          }
                                        : null
                                }
                            />
                        </div>
                        <div className="col">
                            <FormPanel
                                title={t("Common.Contact")}
                                mode="read"
                                pending={
                                    pendingDetails && !deepEqual(getContact(currentDetails), getContact(pendingDetails))
                                }
                                renderRead={() => <Contact details={currentDetails} />}
                                renderEdit={
                                    allowEditInformationDetails
                                        ? (closeEdit) => {
                                              registerFormEditBegin(RelationDetailsAreas.CONTACT);

                                              return (
                                                  <ContactForm
                                                      isSubmittable={!isLoading}
                                                      personDetails={pendingDetails || currentDetails}
                                                      onSubmit={(formValue) => {
                                                          submitRelationDetails(
                                                              {
                                                                  contact: formValue.contact,
                                                              },
                                                              RelationDetailsAreas.CONTACT,
                                                          );
                                                          closeEdit();
                                                      }}
                                                  />
                                              );
                                          }
                                        : null
                                }
                            />
                            <FormPanel
                                title={t("User.PersonalDetails.Profession.Title")}
                                mode="read"
                                pending={
                                    pendingDetails &&
                                    !deepEqual(getProfession(currentDetails), getProfession(pendingDetails))
                                }
                                renderRead={() => <Profession details={currentDetails} />}
                                renderEdit={
                                    allowEditInformationDetails
                                        ? (closeEdit) => {
                                              registerFormEditBegin(RelationDetailsAreas.PROFESSION);

                                              return (
                                                  <ProfessionForm
                                                      isSubmittable={!isLoading}
                                                      personDetails={pendingDetails || currentDetails}
                                                      onSubmit={(formValue) => {
                                                          submitRelationDetails(
                                                              formValue,
                                                              RelationDetailsAreas.PROFESSION,
                                                          );
                                                          closeEdit();
                                                      }}
                                                  />
                                              );
                                          }
                                        : null
                                }
                            />
                        </div>
                    </div>
                )}
            </StyledRelationDetailsPage>
        </StandardLayout>
    );
};

export default RelationDetailsPage;
